import { UniqueIdentifier } from "@dnd-kit/core";
import { useCheckPermission, useUserSetting } from "misc/customHooks";
import { PermissionValues } from "misc/permission.types";
import { PERMISSIONS } from "misc/permissionHelpers";
import { DashboardItemName, DashboardItemsOrder, DashboardItemWidths, UserSettingKey } from "pages/SharedRecords.types";
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useState } from "react";

type DashboardEditContextValueType = {
  isEditingDashboard: boolean;
  initiateDashboardEdit: () => void;
  isFetchingDashboardSettings: boolean;
  dashboardItemWidths: DashboardItemWidths;
  handleDashboardItemWidthChange: (componentName: UniqueIdentifier, width: number) => void;
  orderedDashboardItems: DashboardItemsOrder;
  setDashboardItemsOrder: Dispatch<SetStateAction<DashboardItemName[]>>;
  persistDashboardLayout: () => void;
  cancelDashboardEdit: () => void;
};

const DEFAULT_DASHBOARD_ITEMS_ORDER: DashboardItemsOrder = [
  DashboardItemName.ActionBar,
  DashboardItemName.EventOverview,
  DashboardItemName.Todos,
  DashboardItemName.Timeline,
  DashboardItemName.BookingOverview,
  DashboardItemName.BookingsPerMetric,
  DashboardItemName.BillableEvents,
];

const DEFAULT_DASHBOARD_ITEM_WIDTHS: DashboardItemWidths = {
  actionBar: 12,
  eventOverview: 8,
  todos: 8,
  timeline: 4,
  bookingOverview: 6,
  bookingsPerMetric: 6,
  billableEvents: 8,
};

const defaultContextData: DashboardEditContextValueType = {
  isEditingDashboard: false,
  initiateDashboardEdit: () => {},
  isFetchingDashboardSettings: false,
  dashboardItemWidths: DEFAULT_DASHBOARD_ITEM_WIDTHS,
  orderedDashboardItems: DEFAULT_DASHBOARD_ITEMS_ORDER,
  handleDashboardItemWidthChange: () => {},
  setDashboardItemsOrder: () => {},
  persistDashboardLayout: () => {},
  cancelDashboardEdit: () => {},
};

export const DashboardEditContextValue = createContext<DashboardEditContextValueType>(defaultContextData);

export default function DashboardEditContext(props: PropsWithChildren) {
  const { children } = props;

  const [isEditingDashboard, setIsEditingDashboard] = useState<boolean>(false);
  const [previousDashboardItemsOrder, setPreviousDashboardItemsOrder] = useState<DashboardItemsOrder>();
  const [previousDashboardItemsWidths, setPreviousDashboardItemsWidths] = useState<DashboardItemWidths>();
  const checkPermission = useCheckPermission();

  const {
    settingValue: orderedDashboardItems,
    persistSetting: persistDashboardItemsOrder,
    isFetching: isFetchingDashboardItemsOrder,
    setSettingValue: setDashboardItemsOrder,
  } = useUserSetting<DashboardItemsOrder>(UserSettingKey.DashboardItemsOrder, DEFAULT_DASHBOARD_ITEMS_ORDER);

  const {
    settingValue: dashboardItemWidths,
    persistSetting: persistDashboardItemWidths,
    isFetching: isFetchingDashboardItemWidths,
    setSettingValue: setDashboardItemWidths,
  } = useUserSetting<DashboardItemWidths>(UserSettingKey.DashboardItemWidths, DEFAULT_DASHBOARD_ITEM_WIDTHS);

  const persistDashboardLayout = () => {
    persistDashboardItemsOrder(orderedDashboardItems);
    persistDashboardItemWidths(dashboardItemWidths);
    setIsEditingDashboard(false);
  };

  const cancelDashboardEdit = () => {
    if (previousDashboardItemsOrder) {
      setDashboardItemsOrder(previousDashboardItemsOrder);
    }
    if (previousDashboardItemsWidths) {
      setDashboardItemWidths(previousDashboardItemsWidths);
    }
    setIsEditingDashboard(false);
  };

  const handleDashboardItemWidthChange = (componentName: UniqueIdentifier, width: number) => {
    setDashboardItemWidths({ ...dashboardItemWidths, [componentName]: width });
  };

  const isFetchingDashboardSettings = isFetchingDashboardItemWidths || isFetchingDashboardItemsOrder;

  const initiateDashboardEdit = () => {
    setPreviousDashboardItemsOrder(orderedDashboardItems);
    setPreviousDashboardItemsWidths(dashboardItemWidths);
    setIsEditingDashboard(true);
  };

  // Some items may have been filtered by permissions previously and are now allowed again
  const readdedItems = DEFAULT_DASHBOARD_ITEMS_ORDER.filter((i) => !orderedDashboardItems.includes(i));
  const completeOrderedDashboardItems = orderedDashboardItems.concat(readdedItems);

  const neededPermissionsPerComponentId: { [key: string]: PermissionValues[] | undefined } = {
    billableEvents: [PERMISSIONS.MANAGE_INVOICES],
  };

  const finalOrderedDashboardItems = completeOrderedDashboardItems.filter((i) =>
    checkPermission(neededPermissionsPerComponentId[i]),
  );

  const contextValue = {
    initiateDashboardEdit,
    isEditingDashboard,
    isFetchingDashboardSettings,
    dashboardItemWidths,
    handleDashboardItemWidthChange,
    orderedDashboardItems: finalOrderedDashboardItems,
    setDashboardItemsOrder,
    persistDashboardLayout,
    cancelDashboardEdit,
  };
  return <DashboardEditContextValue.Provider value={contextValue}>{children}</DashboardEditContextValue.Provider>;
}
